import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  DescriptionList,
  Icon,
  InputSwitch,
  InputText,
  Loading,
  NumberInput,
  RadioPresets,
  SectionCard,
  Select,
  TextArea,
  type SelectOption,
} from "@/deps/ui-library";
import { api } from "@/api";
import { keys, useCheckoutOffer, useCheckoutQuote } from "@/api/queries";
import type {
  CheckoutBilling,
  CheckoutChoice,
  CheckoutCycle,
  CheckoutOffer,
  CheckoutOrder,
  CheckoutPlan,
  CheckoutQuote,
  PaymentMethod,
} from "@/api/types";
import { featureNamed } from "@/app/plan";
import { SegmentControl } from "@/components/SegmentControl";
import { BILLING_CONTACT } from "@/lib/billingContact";
import { cn } from "@/lib/cn";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { SALES_COUNTRIES, salesCountry, taxIdProblem } from "@/lib/countries";
import { PaymentPage } from "./PaymentPage";
import { onlineMethods } from "./paymentMethods";

const STEPS = ["Plan", "Billing details", "Review and pay"] as const;

const CYCLES = [
  { value: "MONTHLY" as const, label: "Monthly" },
  { value: "YEARLY" as const, label: "Yearly" },
];

/** A feature code as people say it. */
export function featureName(code: string): string {
  return featureNamed(code)?.name ?? (code === "REPORTING" ? "Reporting layer" : code);
}

const money = (currency: string, amount: number) => `${currency} ${formatAmount(amount)}`;

type PayMethod = "ONLINE" | "BANK_TRANSFER";

/** "eSewa, Khalti or connectIPS": the online methods offered, as a sentence says them. */
function namesOf(methods: readonly string[] | null | undefined): string {
  const names = onlineMethods(methods).map((m) => m.label);
  return names.length < 2
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} or ${names.at(-1)}`;
}

const PAY_METHODS = [
  {
    value: "ONLINE" as const,
    label: "Pay online",
    description: "On the provider's own secure page. The plan starts at once.",
  },
  {
    value: "BANK_TRANSFER" as const,
    label: "Bank transfer",
    description:
      "Get a pro-forma with Rigo's account and a reference. The plan starts when the transfer arrives.",
  },
];

/** The plan the customer most likely wants first: the one their trial is on, else the middle one. */
function startingChoice(offer: CheckoutOffer): CheckoutChoice {
  const current = offer.plans.find((plan) => plan.code === offer.current.planCode);
  const plan = current ?? offer.plans[Math.floor(offer.plans.length / 2)] ?? offer.plans[0];
  return {
    planCode: plan?.code ?? "",
    cycle: offer.current.cycle === "YEARLY" ? "YEARLY" : "MONTHLY",
    addOns: offer.current.addOns.filter((code) =>
      offer.addOns.some((addOn) => addOn.code === code && plan && addOn.prices[plan.code]),
    ),
    extraUsers: plan?.extraUser ? offer.current.extraUsers : 0,
  };
}

/**
 * Buying a plan (suite ADR-0097, plan slice 10.6): the plan, the billing details for the tax invoice, then review and
 * pay. Every price and total is RigoSub's, quoted as the first bill will charge it; this screen adds nothing up. The
 * order changes nothing until the payment page says it was paid, so leaving halfway leaves the trial as it was.
 */
export function Checkout({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  // The country picks the price book (suite ADR-0101): another one re-prices the offer, until the subscription is paid.
  const [country, setCountry] = useState<string | null>(null);
  const { data: offer, error, isPending } = useCheckoutOffer(country);

  if (isPending) return <Loading />;
  if (error || !offer) {
    return (
      <Alert
        config={{ variant: "subtle", colorScheme: "danger" }}
        title="The plans couldn't be loaded"
        description={
          error instanceof Error ? error.message : `Write to ${BILLING_CONTACT.email} to subscribe.`
        }
      />
    );
  }

  return <CheckoutSteps offer={offer} onCountry={setCountry} onDone={onDone} onCancel={onCancel} />;
}

function CheckoutSteps({
  offer,
  onCountry,
  onDone,
  onCancel,
}: {
  offer: CheckoutOffer;
  onCountry: (country: string) => void;
  onDone: () => void;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [picked, setChoice] = useState<CheckoutChoice>(() => startingChoice(offer));
  // The choice is priced in the offer's country, which RigoSub settles (a paid subscription's is fixed).
  const choice: CheckoutChoice = { ...picked, country: offer.country ?? null };
  const [accepted, setAccepted] = useState(false);
  const [billing, setBilling] = useState<CheckoutBilling>(offer.billing);
  const [order, setOrder] = useState<CheckoutOrder | null>(null);
  const [placing, setPlacing] = useState(false);
  const [refused, setRefused] = useState<string | null>(null);
  const [paid, setPaid] = useState(false);
  // A bank transfer (design D4) is offered where the price book takes one and Rigo's account is configured.
  const byTransferOffered = (offer.paymentMethods ?? []).includes("BANK_TRANSFER");
  // eSewa, Khalti and connectIPS where a gateway takes them (suite ADR-0102); chosen on the payment page.
  const onlineOffered = onlineMethods(offer.paymentMethods).length > 0;
  const [method, setMethod] = useState<PayMethod>(
    onlineOffered || !byTransferOffered ? "ONLINE" : "BANK_TRANSFER",
  );
  const [proForma, setProForma] = useState<CheckoutOrder | null>(null);

  const { data: quote, error: quoteError, isFetching } = useCheckoutQuote(choice);
  const plan = offer.plans.find((p) => p.code === choice.planCode);

  // A different choice is a different list of what locks, so it's confirmed again.
  useEffect(() => setAccepted(false), [choice.planCode, choice.addOns]);

  const losing = quote?.losing ?? [];
  const planReady = !!quote && !quoteError && !isFetching && (losing.length === 0 || accepted);
  const billingErrors = useMemo(() => {
    const errors: Partial<Record<keyof CheckoutBilling, string>> = {};
    if (!billing.name.trim()) errors.name = "The legal name goes on the tax invoice.";
    if (!billing.email?.trim()) errors.email = "Invoices are sent here.";
    const idProblem = billing.taxId?.trim() ? taxIdProblem(offer.country, billing.taxId) : null;
    if (idProblem) errors.taxId = idProblem;
    return errors;
  }, [billing, offer.country]);

  /** The order, through a method: none takes the first online one offered, and the payment page can change it. */
  const place = (total: number, through: PaymentMethod | null) =>
    api.subscription.placeOrder({
      choice,
      billing: {
        name: billing.name.trim(),
        taxId: billing.taxId?.trim() || null,
        address: billing.address?.trim() || null,
        email: billing.email?.trim() || null,
      },
      expectedTotal: total,
      method: through,
    });

  const pay = async () => {
    if (!quote) return;
    setPlacing(true);
    setRefused(null);
    try {
      const placed = await place(quote.total, method === "BANK_TRANSFER" ? "BANK_TRANSFER" : null);
      if (placed.payment.transfer) {
        // Nothing changes until the money arrives; the offer now says what's awaited.
        setProForma(placed);
        void queryClient.invalidateQueries({ queryKey: keys.subscription });
        return;
      }
      setOrder(placed);
    } catch (failure) {
      setRefused(failure instanceof Error ? failure.message : "The order couldn't be placed.");
    } finally {
      setPlacing(false);
    }
  };

  if (proForma?.payment.transfer) {
    return <ProFormaIssued order={proForma} email={billing.email} onDone={onDone} />;
  }

  if (paid && quote && plan) {
    return (
      <Confirmation
        plan={plan}
        choice={choice}
        quote={quote}
        email={billing.email}
        onDone={onDone}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ol
        aria-label="Checkout steps"
        className="m-0 flex list-none flex-wrap gap-x-6 gap-y-1 p-0 text-sm"
      >
        {STEPS.map((label, index) => (
          <li
            key={label}
            aria-current={index === step ? "step" : undefined}
            className={cn(index === step ? "text-fg font-semibold" : "text-fg-muted")}
          >
            {`${index + 1}. ${label}`}
          </li>
        ))}
      </ol>

      {offer.awaitingTransfer ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "info" }}
          title="A bank transfer is on its way"
          description={`Pro-forma ${offer.awaitingTransfer.reference} for ${money(offer.awaitingTransfer.currency, offer.awaitingTransfer.total)}, ordered ${formatDate(offer.awaitingTransfer.orderedOn)}. Your plan starts when Rigo receives it. Placing another order cancels that pro-forma.`}
        />
      ) : null}

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-4">
          {step === 0 ? (
            <PlanStep
              offer={offer}
              choice={choice}
              onCountry={onCountry}
              onChange={setChoice}
              losing={losing}
              accepted={accepted}
              onAccept={setAccepted}
              quoteError={quoteError}
            />
          ) : step === 1 ? (
            <BillingStep
              billing={billing}
              country={offer.country}
              onChange={setBilling}
              errors={billingErrors}
            />
          ) : (
            <ReviewStep
              offer={offer}
              billing={billing}
              quote={quote}
              refused={refused}
              method={method}
              onMethod={byTransferOffered ? setMethod : null}
            />
          )}

          <div className="flex flex-wrap justify-between gap-2">
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              onClick={() => (step === 0 ? onCancel() : setStep(step - 1))}
            >
              {step === 0 ? "Cancel" : "Back"}
            </Button>
            {step < 2 ? (
              <Button
                colorScheme="primary"
                size="sm"
                disabled={step === 0 ? !planReady : Object.keys(billingErrors).length > 0}
                onClick={() => setStep(step + 1)}
              >
                Continue
              </Button>
            ) : (
              <Button
                colorScheme="primary"
                size="sm"
                loading={placing}
                disabled={
                  !quote ||
                  isFetching ||
                  (method === "ONLINE" ? !onlineOffered : !byTransferOffered)
                }
                onClick={() => void pay()}
              >
                {method === "BANK_TRANSFER"
                  ? "Get pro-forma"
                  : quote
                    ? `Pay ${money(quote.currency, quote.total)}`
                    : "Pay"}
              </Button>
            )}
          </div>
        </div>

        <OrderSummary
          offer={offer}
          plan={plan}
          choice={choice}
          quote={quote}
          updating={isFetching}
        />
      </div>

      {order ? (
        <PaymentPage
          order={order}
          replace={(through) => place(order.quote.total, through)}
          onClose={() => setOrder(null)}
          onPaid={() => {
            setOrder(null);
            setPaid(true);
            void queryClient.invalidateQueries({ queryKey: keys.subscription });
          }}
        />
      ) : null}
    </div>
  );
}

function PlanStep({
  offer,
  choice,
  onCountry,
  onChange,
  losing,
  accepted,
  onAccept,
  quoteError,
}: {
  offer: CheckoutOffer;
  choice: CheckoutChoice;
  onCountry: (country: string) => void;
  onChange: (choice: CheckoutChoice) => void;
  losing: string[];
  accepted: boolean;
  onAccept: (accepted: boolean) => void;
  quoteError: Error | null;
}) {
  const yearly = choice.cycle === "YEARLY";
  const plan = offer.plans.find((p) => p.code === choice.planCode);

  const pick = (next: CheckoutPlan) =>
    onChange({
      ...choice,
      planCode: next.code,
      addOns: choice.addOns.filter((code) =>
        offer.addOns.some((a) => a.code === code && a.prices[next.code]),
      ),
      extraUsers: next.extraUser ? choice.extraUsers : 0,
    });

  const sold: SelectOption[] = (offer.countries ?? []).map((c) => ({
    value: c.code,
    label: `${SALES_COUNTRIES.find((known) => known.code === c.code)?.name ?? c.code} · ${c.currency}`,
  }));

  return (
    <>
      {offer.country ? (
        <SectionCard title="Country">
          {offer.countryLocked ? (
            <p className="text-fg m-0 text-sm">
              {`${salesCountry(offer.country).name}, in ${offer.currency}. It can't be changed after subscribing.`}
            </p>
          ) : (
            <Select
              label="Where the company is"
              helperText={
                offer.taxPercent > 0
                  ? `Prices in ${offer.currency}, plus ${offer.taxPercent}% VAT.`
                  : `Prices in ${offer.currency}, with no VAT, paid by card.`
              }
              options={sold}
              value={offer.country}
              showClearButton={false}
              onChange={(details) => {
                const next = Array.isArray(details.value) ? details.value[0] : details.value;
                if (next) onCountry(String(next.value));
              }}
            />
          )}
        </SectionCard>
      ) : null}

      <SectionCard
        title="Plan"
        actions={
          <SegmentControl<CheckoutCycle>
            label="Billing cycle"
            segments={CYCLES}
            value={choice.cycle}
            onChange={(cycle) => onChange({ ...choice, cycle })}
          />
        }
      >
        <RadioPresets.CardRadio
          aria-label="Plan"
          orientation="horizontal"
          className="[&>div>*]:min-w-48 [&>div>*]:flex-1"
          value={choice.planCode}
          onValueChange={(details: { value: string | null }) => {
            const next = offer.plans.find((p) => p.code === details.value);
            if (next) pick(next);
          }}
          items={offer.plans.map((p) => ({
            value: p.code,
            label: `${p.name} · ${money(offer.currency, yearly ? p.price.yearly : p.price.monthly)}${yearly ? " a year" : " a month"}`,
            description: [
              `${p.includedUsers} users included`,
              p.features.length > 0
                ? p.features.map(featureName).join(", ")
                : "All core accounting and standard reports",
              p.code === offer.current.planCode && offer.current.trialEndsOn
                ? "Your trial's plan"
                : null,
            ]
              .filter(Boolean)
              .join(". "),
          }))}
        />
      </SectionCard>

      {plan ? (
        <SectionCard title="Add-ons and users">
          <div className="flex flex-col gap-4">
            {offer.addOns
              .filter((addOn) => addOn.prices[plan.code])
              .map((addOn) => {
                const price = addOn.prices[plan.code]!;
                return (
                  <InputSwitch
                    key={addOn.code}
                    label={`${addOn.name} · ${money(offer.currency, yearly ? price.yearly : price.monthly)}${yearly ? " a year" : " a month"}`}
                    helperText={addOn.description ?? undefined}
                    checked={choice.addOns.includes(addOn.code)}
                    onCheckedChange={(details: { checked: boolean }) =>
                      onChange({
                        ...choice,
                        addOns: details.checked
                          ? [...choice.addOns, addOn.code]
                          : choice.addOns.filter((code) => code !== addOn.code),
                      })
                    }
                  />
                );
              })}
            {plan.extraUser ? (
              <NumberInput
                label="Extra users"
                helperText={`Beyond the ${plan.includedUsers} included, ${money(offer.currency, yearly ? plan.extraUser.yearly : plan.extraUser.monthly)} each${yearly ? " a year" : " a month"}.`}
                min={0}
                max={500}
                value={String(choice.extraUsers)}
                onValueChange={(details: { valueAsNumber: number }) =>
                  onChange({
                    ...choice,
                    extraUsers: Number.isFinite(details.valueAsNumber)
                      ? Math.max(0, Math.trunc(details.valueAsNumber))
                      : 0,
                  })
                }
                className="max-w-48"
              />
            ) : (
              <p className="text-fg-muted m-0 text-sm">{`${plan.name} includes ${plan.includedUsers} users and sells no extra users.`}</p>
            )}
          </div>
        </SectionCard>
      ) : null}

      {quoteError ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={quoteError.message}
        />
      ) : null}

      {losing.length > 0 ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "warning" }}
          title={`On ${plan?.name ?? "this plan"}, these lock: ${losing.map(featureName).join(" · ")}`}
          description={
            <div className="flex flex-col gap-2">
              <span>
                Nothing you've posted is removed. They open again on a plan that has them.
              </span>
              <Checkbox
                label="I understand what will lock"
                checked={accepted}
                onCheckedChange={(details) => onAccept(details.checked === true)}
              />
            </div>
          }
        />
      ) : null}
    </>
  );
}

function BillingStep({
  billing,
  country,
  onChange,
  errors,
}: {
  billing: CheckoutBilling;
  country: string | null | undefined;
  onChange: (billing: CheckoutBilling) => void;
  errors: Partial<Record<keyof CheckoutBilling, string>>;
}) {
  const id = salesCountry(country);
  return (
    <SectionCard title="Billing details">
      <div className="flex flex-col gap-3">
        <p className="text-fg-muted m-0 text-sm">Printed on the tax invoice from Rigo.</p>
        <InputText
          label="Legal name"
          required
          value={billing.name}
          onChange={(e) => onChange({ ...billing, name: e.target.value })}
          errorText={errors.name}
        />
        <InputText
          label={id.idLabel}
          helperText={id.idHint || undefined}
          inputMode={id.digits ? "numeric" : undefined}
          value={billing.taxId ?? ""}
          onChange={(e) => onChange({ ...billing, taxId: e.target.value })}
          errorText={errors.taxId}
        />
        <TextArea
          label="Billing address"
          rows={2}
          value={billing.address ?? ""}
          onChange={(e) => onChange({ ...billing, address: e.target.value })}
        />
        <InputText
          label="Billing email"
          type="email"
          required
          value={billing.email ?? ""}
          onChange={(e) => onChange({ ...billing, email: e.target.value })}
          errorText={errors.email}
        />
      </div>
    </SectionCard>
  );
}

function ReviewStep({
  offer,
  billing,
  quote,
  refused,
  method,
  onMethod,
}: {
  offer: CheckoutOffer;
  billing: CheckoutBilling;
  quote: CheckoutQuote | undefined;
  refused: string | null;
  method: PayMethod;
  /** Null when only paying online is offered. */
  onMethod: ((method: PayMethod) => void) | null;
}) {
  const online = namesOf(quote?.paymentMethods ?? offer.paymentMethods);
  return (
    <>
      <SectionCard title="Billed to">
        <div className="text-fg flex flex-col gap-0.5 text-sm">
          <span className="font-semibold">{billing.name}</span>
          {billing.taxId ? (
            <span>{`${salesCountry(offer.country).idLabel.replace(" (optional)", "")} ${billing.taxId}`}</span>
          ) : null}
          {billing.address ? <span>{billing.address}</span> : null}
          <span className="text-fg-muted">{`Invoices to ${billing.email}`}</span>
        </div>
      </SectionCard>

      <SectionCard title="Payment">
        <div className="flex flex-col gap-2 text-sm">
          {onMethod ? (
            <RadioPresets.CardRadio
              aria-label="How you pay"
              orientation="horizontal"
              className="[&>div>*]:min-w-48 [&>div>*]:flex-1"
              value={method}
              onValueChange={(details: { value: string | null }) => {
                if (details.value === "ONLINE" || details.value === "BANK_TRANSFER") {
                  onMethod(details.value);
                }
              }}
              items={PAY_METHODS.map((m) =>
                m.value === "ONLINE"
                  ? {
                      ...m,
                      description: online
                        ? `${online} on their own secure page. The plan starts at once.`
                        : "Not open yet.",
                      disabled: !online,
                    }
                  : m,
              )}
            />
          ) : null}
          {method === "BANK_TRANSFER" ? (
            <p className="text-fg m-0">
              {`You get a pro-forma for the total, in ${offer.currency}, with Rigo's bank account and a reference to quote. Nothing changes until the transfer arrives and Rigo's billing team records it; the trial runs on meanwhile.`}
            </p>
          ) : online ? (
            <p className="text-fg m-0">
              {`You choose ${online} next, approve the payment on its own page, and come back here. Rigo Accounting never sees your password or card.`}
            </p>
          ) : (
            <Alert
              config={{ variant: "subtle", colorScheme: "warning" }}
              description={`Paying online isn't open yet. Write to ${BILLING_CONTACT.email} to subscribe.`}
            />
          )}
          {quote ? (
            <p className="text-fg-muted m-0">
              {`${method === "BANK_TRANSFER" ? "The transfer ends the free trial, and billing counts from today, the day of the order." : "Paying now ends the free trial and starts billing today."} It renews on ${formatDate(quote.renewsOn)}. No refunds: cancelling stops the next renewal, and the plan runs to the end of what's paid.`}
            </p>
          ) : null}
        </div>
      </SectionCard>

      {refused ? (
        <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={refused} />
      ) : null}
    </>
  );
}

function OrderSummary({
  offer,
  plan,
  choice,
  quote,
  updating,
}: {
  offer: CheckoutOffer;
  plan: CheckoutPlan | undefined;
  choice: CheckoutChoice;
  quote: CheckoutQuote | undefined;
  updating: boolean;
}) {
  const yearly = choice.cycle === "YEARLY";
  return (
    <SectionCard title="Order summary" className="lg:sticky lg:top-4">
      {!quote || !plan ? (
        <Loading />
      ) : (
        <div
          className={cn("flex flex-col gap-3 text-sm", updating && "opacity-60")}
          aria-busy={updating}
        >
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {quote.lines.map((line) => (
              <li key={`${line.label}${line.detail}`} className="flex justify-between gap-3">
                <span className="flex flex-col">
                  <span className="text-fg">{line.label}</span>
                  <span className="text-fg-muted text-xs">{line.detail}</span>
                </span>
                <span className="text-fg num">{formatAmount(line.amount)}</span>
              </li>
            ))}
          </ul>
          <div className="border-line-soft flex flex-col gap-1 border-t pt-2">
            <div className="flex justify-between">
              <span className="text-fg-muted">Subtotal</span>
              <span className="num">{formatAmount(quote.subtotal)}</span>
            </div>
            {quote.taxPercent > 0 ? (
              <div className="flex justify-between">
                <span className="text-fg-muted">{`VAT ${quote.taxPercent}%`}</span>
                <span className="num">{formatAmount(quote.tax)}</span>
              </div>
            ) : (
              <div className="flex justify-between">
                <span className="text-fg-muted">No VAT</span>
                <span className="num">{formatAmount(0)}</span>
              </div>
            )}
            <div className="text-fg flex justify-between font-semibold">
              <span>Due today</span>
              <span className="num">{money(quote.currency, quote.total)}</span>
            </div>
          </div>
          <p className="text-fg-muted m-0 text-xs">
            {`Covers ${formatDate(quote.periodStart)} to ${formatDate(quote.periodEnd)}. From ${formatDate(quote.renewsOn)}, ${money(offer.currency, quote.perCycle)} ${yearly ? "a year" : "a month"}${quote.taxPercent > 0 ? " plus VAT" : ""}.`}
          </p>
        </div>
      )}
    </SectionCard>
  );
}

function Confirmation({
  plan,
  choice,
  quote,
  email,
  onDone,
}: {
  plan: CheckoutPlan;
  choice: CheckoutChoice;
  quote: CheckoutQuote;
  email: string | null;
  onDone: () => void;
}) {
  const what = [
    plan.name,
    ...choice.addOns.map(featureName),
    ...(choice.extraUsers > 0 ? [`${choice.extraUsers} extra users`] : []),
  ].join(" + ");
  return (
    <Card variant="outlined" className="flex flex-col items-start gap-3 p-6">
      <Icon name="CircleCheck" size="md" className="text-good-text" />
      <h2 className="text-fg m-0 text-lg font-semibold">{`You're subscribed: ${what}`}</h2>
      <p className="text-fg m-0 text-sm">
        {`Billed ${choice.cycle === "YEARLY" ? "yearly" : "monthly"}. The next invoice is on ${formatDate(quote.renewsOn)}.${email ? ` The tax invoice for today's payment is emailed to ${email}.` : ""}`}
      </p>
      <p className="text-fg-muted m-0 text-sm">
        Everything from the trial is kept, and the trial banner is gone.
      </p>
      <Button colorScheme="primary" size="sm" onClick={onDone}>
        Go to subscription
      </Button>
    </Card>
  );
}

/** The pro-forma for a bank transfer (design D4): what to send, where, and the reference that matches it. */
function ProFormaIssued({
  order,
  email,
  onDone,
}: {
  order: CheckoutOrder;
  email: string | null;
  onDone: () => void;
}) {
  const transfer = order.payment.transfer;
  if (!transfer) return null;
  return (
    <Card variant="outlined" className="flex flex-col items-start gap-3 p-6">
      <Icon name="Landmark" size="md" className="text-fg-muted" />
      <h2 className="text-fg m-0 text-lg font-semibold">{`Pro-forma ${order.payment.reference}`}</h2>
      <p className="text-fg m-0 text-sm">
        {`Transfer ${money(order.quote.currency, order.quote.total)} to the account below and quote ${order.payment.reference} as the reference. Your plan starts when Rigo's billing team records the transfer, counted from today, and renews on ${formatDate(order.quote.renewsOn)}. Until then the trial runs on.`}
      </p>
      <DescriptionList
        variant="none"
        compact
        flush
        cardBordered={false}
        items={[
          { label: "Amount", value: money(order.quote.currency, order.quote.total) },
          { label: "Reference", value: order.payment.reference },
          { label: "Account name", value: transfer.accountName },
          { label: "Bank", value: transfer.bankName },
          { label: "Account number", value: transfer.accountNumber },
          ...(transfer.branch ? [{ label: "Branch", value: transfer.branch }] : []),
          ...(transfer.swiftCode ? [{ label: "SWIFT", value: transfer.swiftCode }] : []),
        ]}
      />
      <p className="text-fg-muted m-0 text-sm">
        {`The tax invoice is issued when the transfer is recorded${email ? ` and emailed to ${email}` : ""}. Paying online instead cancels this pro-forma.`}
      </p>
      <Button colorScheme="primary" size="sm" onClick={onDone}>
        Go to subscription
      </Button>
    </Card>
  );
}
